import React from 'react';
import styled from 'styled-components';
import theme, { mobileBreakpoint } from '../theme';
import PageLayout from './PageLayout';
import { useAuth } from '../AuthContext';
import { useEffect, useState } from 'react';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import LessonBookingPopup from './LessonBookingPopup';
import ConfirmationPopup from './ConfirmationPopup';
import { LessonStatus, InformationText, PaginationContainer, PaginationButton, AcceptButton, PaginationInfo, ActionButton, SecondaryButton, PrimaryButton, ManageButton, DataManageButton, SecondaryDataManageButton, StarBadge, MessageStrip, MessageIcon, MessageText } from './shared/CommonElements';

const Container = styled.div`
  max-width: 800px;
  margin: 40px auto 0 auto;
  padding: 32px 16px 64px 16px;
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
  margin-top: -5px;
`;

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  
`;



const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 1000;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 1000;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;
const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 32px;
  color: ${props => props.theme.colors.primary};
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  color: ${theme.colors.primary};
  font-weight: 600;
  margin: 32px 0 16px 0;
`;

const LessonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const LessonCard = styled.div`
  background: ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return 'rgba(188, 231, 253, 0.13)';
      case 'ADULTS':
        return 'rgba(196, 146, 177, 0.08)';
      case 'SPECIAL':
        return 'rgba(219, 39, 98, 0.04)';
      default:
        return '#f5f5f5';
    }
  }};
  border: 1px solid ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return '#bce7fd';
      case 'ADULTS':
        return '#c492b1';
      case 'SPECIAL':
        return '#db2762';
      default:
        return '#e0e0e0';
    }
  }};
  border-radius: ${theme.borderRadius.medium};
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
`;

const LessonTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin: 0 0 12px 0;
`;



const SkeletonBudgetTypeBox = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  min-width: 220px;
  padding: 18px 24px;
  text-align: center;
  box-shadow: 2px 2px 6px rgba(0,0,0,0.04);
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: 250px;
  }
`;

const SkeletonBudgetTypeLabel = styled.div`
  height: 16px;
  width: 120px;
  background: #e0e0e0;
  border-radius: 4px;
  margin: 0 auto 6px auto;
`;

const SkeletonBudgetTypeAmount = styled.div`
  height: 32px;
  width: 80px;
  background: #e0e0e0;
  border-radius: 4px;
  margin: 0 auto;
`;

const SkeletonLessonCard = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;

const SkeletonLessonTitle = styled.div`
  height: 24px;
  width: 200px;
  background: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 12px;
`;

const SkeletonLessonInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const SkeletonLessonInfoItem = styled.div`
  height: 16px;
  width: 120px;
  background: #e0e0e0;
  border-radius: 4px;
`;

const ManageButtonSpecial = styled(ManageButton)`
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: 260px;
  }
`;

const SkeletonManageButton = styled.div`
  height: 36px;
  width: 120px;
  background: #e0e0e0;
  border-radius: ${theme.borderRadius.medium};
  align-self: flex-end;
  margin-top: 8px;
`;

const LessonInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  color: ${theme.colors.primary};
  font-size: 1rem;

  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    gap: 6px;
  }
  
  span {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    b {
      white-space: nowrap;
      flex-shrink: 0;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-self: flex-end;
  
  @media (max-width: ${mobileBreakpoint}px) {
    align-self: flex-end;
    justify-content: flex-end;
  }
`;





const BudgetAmount = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${theme.colors.primary};
  margin-bottom: 8px;
`;



const BudgetsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  justify-content: center;
  margin-bottom: 18px;
`;

const BudgetTypeBox = styled.div`
  background: linear-gradient(135deg, #e3f6fd 0%, #f8e1f6 100%);
  border-radius: ${theme.borderRadius.medium};
  border: 0px solid ${theme.colors.accent2};
  min-width: 220px;
  padding: 18px 24px;
  text-align: center;
  box-shadow: 2px 2px 6px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  &:hover {
    transform: scale(1.025);
    box-shadow: 5px 5px 20px rgba(96, 80, 85, 0.11);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: 250px;
  }
`;

const BudgetTypeLabel = styled.div`
  color: ${theme.colors.secondary};
  font-size: 1.08rem;
  font-weight: 600;
  margin-bottom: 6px;
`;

const BudgetTypeAmount = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.primary};
`;

const DataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 1.15rem;
  color: ${theme.colors.primary};
`;

const EditDataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 1.15rem;
  color: ${theme.colors.primary};
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    .input {
      width: 100% !important;
      margin-left: 0 !important;
    }
    .label {
      min-width: unset;
    }
  }
  @media (min-width: 769px) {
    .label {
      min-width: 160px;
      flex-shrink: 0;
    }
    .input {
      flex: 1;
    }
  }
`;

const EditDataRowShifted = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 1.15rem;
  color: ${theme.colors.primary};
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    .input {
      width: calc(100% - 38px) !important;
      margin-left: 0 !important;
    }
    .label {
      min-width: unset;
    }
  }
  @media (min-width: 769px) {
    .label {
      min-width: 160px;
      flex-shrink: 0;
    }
  }
`;

const EditDataRowShiftedEmoji = styled(EditDataRowShifted)`
  @media (max-width: 768px) {
    .input {
      width: 100% !important;
    }
  }
`;

const AcceptButtonReschedule = styled(AcceptButton)`
  min-width: 260px;
  @media (max-width: ${mobileBreakpoint}px) {
    width: unset;
  }
`;


const DataIcon = styled.span`
  font-size: 1.5rem;
  color: ${theme.colors.secondary};
  display: flex;
  align-items: center;
`;

const DataIconEdit = styled(DataIcon)`
  @media (max-width: 768px) {
    transform: translateY(-22px);
  }
`;

const sampleLessons = [
  {
    id: 1,
    date: '2024-08-12',
    time: '16:00 - 16:50',
    status: 'Bestätigt',
    topic: 'Konversation',
  },
  {
    id: 2,
    date: '2024-08-19',
    time: '17:00 - 17:50',
    status: 'Ausstehend',
    topic: 'Grammatik',
  },
  {
    id: 3,
    date: '2024-08-26',
    time: '15:00 - 15:50',
    status: 'Bestätigt',
    topic: 'Business English',
  },
];

const sampleBudgets = [
  { type: 'Einzelstunden', amount: 3 },
  { type: 'Gruppenstunden', amount: 2 },
  { type: 'Business English', amount: 1 },
];

// User billing data structure
const userBillingData = {
  name: '-',
  street: '-',
  zipCode: '-',
  city: '-'
};

const MyPage = () => {
  const { token, isAuthenticated, logout } = useAuth();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const lessonsPerPage = 5;
  const [userPermissions, setUserPermissions] = useState(null);
  const [userPermissionsLoading, setUserPermissionsLoading] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Lesson booking popup state
  const [isLessonBookingOpen, setIsLessonBookingOpen] = useState(false);

  // Billing data state and edit mode
  const [billingData, setBillingData] = useState(userBillingData);
  const [originalBillingData, setOriginalBillingData] = useState(userBillingData);
  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const [billingDataLoading, setBillingDataLoading] = useState(false);
  const [billingDataError, setBillingDataError] = useState(null);

  // Account closure popup state
  const [isAccountClosureOpen, setIsAccountClosureOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isClosingAccount, setIsClosingAccount] = useState(false);
  const [accountClosureError, setAccountClosureError] = useState('');

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingData(prev => ({ ...prev, [name]: value }));
  };

  const handleBillingEdit = () => setIsEditingBilling(true);
  
  const handleBillingCancel = () => {
    // Reset billing data to original loaded values and exit edit mode
    setBillingData(originalBillingData);
    setIsEditingBilling(false);
    setBillingDataError(null);
  };

  const handleAccountClosure = async () => {
    if (confirmationText !== "Ich verstehe") {
      return; // This shouldn't happen as the button should be disabled
    }

    setIsClosingAccount(true);
    setAccountClosureError('');
    
    try {
      const response = await fetch(config.getApiUrl('/api/auth/deleteUser'), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Account closed successfully, logout user
        setIsAccountClosureOpen(false);
        logout();
        // Navigate to home page after logout
        navigate('/');
        return; // Exit early to avoid setting loading to false
      } else {
        const errorText = await response.text();
        setAccountClosureError(errorText || 'Fehler beim Schließen des Kontos. Bitte versuche es erneut.');
      }
    } catch (err) {
      setAccountClosureError('Netzwerkfehler. Bitte versuche es erneut.');
    } finally {
      // Only set loading to false if we're not logging out
      if (isAccountClosureOpen) {
        setIsClosingAccount(false);
      }
    }
  };

  const handleAccountClosureClose = () => {
    if (!isClosingAccount) {
      setConfirmationText('');
      setAccountClosureError('');
      setIsAccountClosureOpen(false);
    }
  };
  
  const handleBillingSave = async () => {
    setBillingDataLoading(true);
    setBillingDataError(null);
    
    try {
      const res = await fetch(config.getApiUrl('/api/mypage/billing-data'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(billingData)
      });
      
      if (res.ok) {
        setIsEditingBilling(false);
        setOriginalBillingData(billingData); // Update original data after successful save
      } else {
        // Try to get the error message from the backend response
        try {
          const errorText = await res.text();
          setBillingDataError(errorText || 'Fehler beim Speichern der Daten');
        } catch (parseError) {
          setBillingDataError('Fehler beim Speichern der Daten');
        }
      }
    } catch (err) {
      setBillingDataError('Netzwerkfehler beim Speichern');
    } finally {
      setBillingDataLoading(false);
    }
  };

  // Fetch billing data on component mount
  useEffect(() => {
    const fetchBillingData = async () => {
      if (!isAuthenticated() || !token) return;
      
      // Small delay to ensure auth state is stable
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setBillingDataLoading(true);
      setBillingDataError(null);
      
      try {
        const res = await fetch(config.getApiUrl('/api/mypage/billing-data'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (res.ok) {
          const data = await res.json();
          setBillingData(data);
          setOriginalBillingData(data); // Store the loaded data as original
        } else if (res.status === 401) {
          setBillingDataError('Nicht autorisiert. Bitte melden Sie sich erneut an.');
        } else {
          setBillingDataError('Fehler beim Laden der Daten');
        }
      } catch (err) {
        setBillingDataError('Netzwerkfehler beim Laden');
      } finally {
        setBillingDataLoading(false);
      }
    };
    
    fetchBillingData();
  }, [token, isAuthenticated]);



  const navigate = useNavigate();

  const handleOpenPackagePopup = () => {
    navigate('/buy');
  };

  const handleOpenLessonBooking = () => {
    setIsLessonBookingOpen(true);
  };

  const handleCloseLessonBooking = () => {
    setIsLessonBookingOpen(false);
  };

  const handleLessonBooked = (bookedLessonType) => {
    // Reload lessons after successful booking
    fetchLessons();
    setCurrentPage(1); // Reset to first page when new lesson is booked
    
    // Update budget for the booked lesson type
    if (pageData?.budgets && bookedLessonType) {
      setPageData(prevData => ({
        ...prevData,
        budgets: prevData.budgets.map(budget => {
          // Match by lesson type name (case-insensitive)
          if (budget.type.toLowerCase() === bookedLessonType.toLowerCase()) {
            return {
              ...budget,
              amount: Math.max(0, parseInt(budget.amount) - 1).toString()
            };
          }
          return budget;
        })
      }));
    }
  };

  const handleOpenPastLessons = () => {
    navigate('/past-lessons');
  };

  const handleAcceptRescheduleRequest = async (lessonId) => {
    try {
      const response = await fetch(config.getApiUrl(`/api/lessons/${lessonId}/accept-reschedule`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh the lessons list to show updated status
        fetchLessons();
      } else {
        console.error('Failed to accept reschedule request');
      }
    } catch (err) {
      console.error('Error accepting reschedule request:', err);
    }
  };

  // Convert technical status to human-readable names
  const getStatusName = (status) => {
    switch (status) {
      case 'UNCONFIRMED':
        return 'Ausstehend';
      case 'CONFIRMED':
        return 'Bestätigt';
      case 'CANCELLED':
        return 'Storniert';
      case 'DONE':
        return 'Abgeschlossen';
      case 'RESCHEDULE_REQUESTED':
        return 'Verschiebung angefragt';
      case 'SCHEDULING':
        return 'Geplant';
      default:
        return status; // Fallback to original status if unknown
    }
  };

  // Format date to German format (weekday, day month year)
  const formatGermanDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(lessons.length / lessonsPerPage);
  const startIndex = (currentPage - 1) * lessonsPerPage;
  const endIndex = startIndex + lessonsPerPage;
  const currentLessons = lessons.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };



  // Fetch lessons from backend
  const fetchLessons = async () => {
    if (!isAuthenticated() || !token) return;
    
    setLessonsLoading(true);
    try {
      const res = await fetch(config.getApiUrl('/api/lessons/user-lessons'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLessons(data.lessons || []);
      } else if (res.status === 401) {
        setLessons([]);
      } else {
        setLessons([]);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setLessons([]);
    } finally {
      setLessonsLoading(false);
    }
  };

  useEffect(() => {
    const fetchMyPageData = async () => {
      if (!isAuthenticated() || !token) return;
      
      // Small delay to ensure auth state is stable
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setLoading(true);
      try {
        const res = await fetch(config.getApiUrl('/api/mypage'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setPageData(data);
        } else if (res.status === 401) {
          setPageData(null);
          // Could redirect to login here if needed
        } else {
          setPageData(null);
        }
      } catch {
        setPageData(null);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchData = async () => {
      if (isAuthenticated() && token) {
        await fetchMyPageData();
        await fetchLessons();
      } else {
        setLoading(false);
        setLessonsLoading(false);
      }
    };
    
    fetchData();
  }, [token, isAuthenticated]);

  // Fetch user account details for permissions
  useEffect(() => {
    if (!token) return;

    setUserPermissionsLoading(true);
    fetch(config.getApiUrl('/api/mypage/user-info'), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Fehler beim Laden der Benutzerberechtigungen');
        return res.json();
      })
      .then(data => {
        setUserPermissions({
          packagePurchaseAllowed: data.packagePurchaseAllowed,
          socialPriceEligible: data.socialPriceEligible
        });
        setUserPermissionsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching user account details:', err);
        setUserPermissionsLoading(false);
      });
  }, [token]);

  return (
    <PageLayout>
      <HeroSection>
        <HeroTitle>
          <StarBadge>
            <svg width="38" height="38" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 17.27L18.18 21L17 14.14L22 9.27L15.09 8.26L12 2L8.91 8.26L2 9.27L7 14.14L5.82 21L12 17.27Z"/>
            </svg>
          </StarBadge>
          <MainTitle>Mein Bereich</MainTitle>
        </HeroTitle>
        <Subtitle>Deine Informationen auf einen Blick</Subtitle>
        <Description>Verwalte deine Termine, buche neue Stunden und behalte den Überblick über deine Englisch-Lernreise.</Description>
      </HeroSection>
      {loading ? (
        <>
          <Container>
            <Title>Stunden-Budget</Title>
            <BudgetsGrid>
              <SkeletonBudgetTypeBox>
                <SkeletonBudgetTypeLabel />
                <SkeletonBudgetTypeAmount />
              </SkeletonBudgetTypeBox>
              <SkeletonBudgetTypeBox>
                <SkeletonBudgetTypeLabel />
                <SkeletonBudgetTypeAmount />
              </SkeletonBudgetTypeBox>
            </BudgetsGrid>
            <InformationText style={{marginBottom: '0px', marginTop: '30px'}}>Du kannst Stundenpakete für verschiedene Kursarten kaufen und dann flexibel einzelne Stunden buchen.</InformationText>
            <div style={{ display: 'flex', gap: '0px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '-20px' }}>
              <SecondaryButton onClick={() => navigate('/purchased-packages')}>
                Gekaufte Pakete anzeigen
              </SecondaryButton>
              {(!userPermissions || userPermissions.packagePurchaseAllowed) && (
                <PrimaryButton onClick={handleOpenPackagePopup}>Stundenpaket kaufen</PrimaryButton>
              )}
            </div>
          </Container>
          <Container>
            <Title>Meine gebuchten Stunden</Title>
            <LessonsList>
              <SkeletonLessonCard>
                <SkeletonLessonTitle />
                <SkeletonLessonInfo>
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                </SkeletonLessonInfo>
                <SkeletonManageButton />
              </SkeletonLessonCard>
              <SkeletonLessonCard>
                <SkeletonLessonTitle />
                <SkeletonLessonInfo>
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                </SkeletonLessonInfo>
                <SkeletonManageButton />
              </SkeletonLessonCard>
              <SkeletonLessonCard>
                <SkeletonLessonTitle />
                <SkeletonLessonInfo>
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                  <SkeletonLessonInfoItem />
                </SkeletonLessonInfo>
                <SkeletonManageButton />
              </SkeletonLessonCard>
            </LessonsList>
            <div style={{ display: 'flex', gap: '0px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '-20px' }}>
              <SecondaryButton onClick={handleOpenPastLessons}>Vergangene Stunden</SecondaryButton>
              <PrimaryButton onClick={handleOpenLessonBooking}>Neue Stunde buchen</PrimaryButton>
            </div>
          </Container>
          <div style={{textAlign: 'center', margin: '20px 0', color: '#888'}}>Lade deine Daten ...</div>
        </>
      ) : (
        <>
          <Container>
            <Title>Stunden-Budget</Title>
            <BudgetsGrid>
              {pageData?.budgets?.map((b, i) => (
                <BudgetTypeBox key={i}>
                  <BudgetTypeLabel>{b.type}</BudgetTypeLabel>
                  <BudgetTypeAmount>{b.amount} {b.amount === "1" ? 'Stunde' : 'Stunden'}</BudgetTypeAmount>
                </BudgetTypeBox>
              ))}
            </BudgetsGrid>
            <InformationText style={{marginBottom: '0px', marginTop: '30px'}}>Du kannst Stundenpakete für verschiedene Kursarten kaufen und dann flexibel einzelne Stunden buchen.</InformationText>
            
            {/* Package Purchase Permission Warning */}
            {!userPermissionsLoading && userPermissions && !userPermissions.packagePurchaseAllowed && (
              <MessageStrip style={{marginBottom: '0px', marginTop: '20px'}} variant="warning" compact>
              <MessageIcon role="img" aria-label="Hinweis">⚠️</MessageIcon>
              <MessageText>
              Du bist derzeit nicht berechtigt, Stundenpakete zu kaufen. Bitte kontaktiere mich unter{' '}
              <a href="mailto:mey@meyteaches.de" style={{ color: '#b26a00', textDecoration: 'underline' }}>mey@meyteaches.de</a>.
              </MessageText>
            </MessageStrip>
            )}
            
            <div style={{ display: 'flex', gap: '0px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '-20px' }}>
              <SecondaryButton onClick={() => navigate('/purchased-packages')}>
                Gekaufte Pakete anzeigen
              </SecondaryButton>
              {(!userPermissions || userPermissions.packagePurchaseAllowed) && (
                <PrimaryButton onClick={handleOpenPackagePopup}>Stundenpaket kaufen</PrimaryButton>
              )}
            </div>
          </Container>
          <Container>
            <Title>Meine gebuchten Stunden</Title>
            <LessonsList>
              {lessonsLoading ? (
                <div style={{textAlign: 'center', padding: '20px', color: '#888'}}>
                  Lade Stunden...
                </div>
              ) : lessons.length > 0 ? (
                <>
                  {currentLessons.map(lesson => (
                    <LessonCard key={lesson.id} category={lesson.category}>
                      <LessonTitle>{lesson.topic}</LessonTitle>
                      <LessonInfo>
                        <span><b>Datum:</b> {formatGermanDate(lesson.date)}</span>
                        <span><b>Zeit:</b> {lesson.time} Uhr</span>
                        <span><b>Status:</b> <LessonStatus status={getStatusName(lesson.status)}>{getStatusName(lesson.status)}</LessonStatus></span>
                      </LessonInfo>
                      {lesson.status === 'RESCHEDULE_REQUESTED' ? (
                        <ButtonContainer>
                          <AcceptButtonReschedule
                            onClick={() => handleAcceptRescheduleRequest(lesson.id)}
                          >
                            Verschiebung akzeptieren
                          </AcceptButtonReschedule>
                          <ManageButtonSpecial onClick={() => navigate(`/manage-lesson/${lesson.id}`)}>
                            Stunde verwalten
                          </ManageButtonSpecial>
                        </ButtonContainer>
                      ) : (
                        <ManageButton onClick={() => navigate(`/manage-lesson/${lesson.id}`)}>
                          Stunde verwalten
                        </ManageButton>
                      )}
                    </LessonCard>
                  ))}
                  
                  {totalPages > 1 && (
                    <PaginationContainer>
                      <PaginationButton 
                        onClick={handlePreviousPage} 
                        disabled={currentPage === 1}
                      >
                        ←
                      </PaginationButton>
                      
                      {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                        <PaginationButton
                          key={page}
                          onClick={() => handlePageChange(page)}
                          active={currentPage === page}
                        >
                          {page}
                        </PaginationButton>
                      ))}
                      
                      <PaginationButton 
                        onClick={handleNextPage} 
                        disabled={currentPage === totalPages}
                      >
                        →
                      </PaginationButton>
                    </PaginationContainer>
                  )}
                </>
              ) : (
                <InformationText
   style={{marginBottom: '-20px'}}>
                  Du hast aktuell keine Stunden gebucht. Nachdem du ein Stundenpaket gekauft hast, kannst du hier deine Stunden buchen und verwalten.
                </InformationText
  >
              )}
            </LessonsList>
            <div style={{ display: 'flex', gap: '0px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '-20px', marginTop: '20px' }}>

              <SecondaryButton onClick={handleOpenPastLessons}>Vergangene Stunden</SecondaryButton>
              <PrimaryButton onClick={handleOpenLessonBooking}>Neue Stunde buchen</PrimaryButton>
            </div>
          </Container>
          <Container>
          <Title>Meine Daten</Title>
          {/* Show message if any billing field is empty */}
          {(!billingData.name || !billingData.street || !billingData.zipCode || !billingData.city) && !isEditingBilling && (
            <MessageStrip variant="warning" compact>
              <MessageIcon role="img" aria-label="Hinweis">⚠️</MessageIcon>
              <MessageText>
                Bitte vervollständige deine Daten, um Stundenpakete kaufen zu können.
              </MessageText>
            </MessageStrip>
          )}
            <LessonCard style={{marginBottom: '-20px'}}>
                {billingDataError && (
                  <div style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
                    {billingDataError}
                  </div>
                )}
                <DataRow>
                  {isEditingBilling ? <DataIconEdit>👤</DataIconEdit> : <DataIcon>👤</DataIcon>}
                  {isEditingBilling ? (
                   <EditDataRowShiftedEmoji>
                     <span className="label" style={{minWidth: '200px'}}><b>Vollständiger Name:</b></span>
                     <input
                       type="text"
                       name="name"
                       value={billingData.name}
                       onChange={handleBillingChange}
                       className="input"
                       style={{
                         fontSize: '1.1rem',
                         padding: '8px 12px',
                         border: `2px solid ${theme.colors.accent1}`,
                         borderRadius: theme.borderRadius.medium,
                         marginLeft: 8,
                         width: '100%'
                       }}
                     />
                   </EditDataRowShiftedEmoji>
                  ) : (
                    <span><b>Vollständiger Name:</b> {billingData.name}</span>
                  )}
                </DataRow>
                <DataRow>
                  {isEditingBilling ? <DataIconEdit>🏠</DataIconEdit> : <DataIcon>🏠</DataIcon>}
                  {isEditingBilling ? (
                   <EditDataRowShiftedEmoji>
                     <span className="label" style={{minWidth: '200px'}}><b>Straße:</b></span>
                     <input
                       type="text"
                       name="street"
                       value={billingData.street}
                       onChange={handleBillingChange}
                       className="input"
                       style={{
                         fontSize: '1.1rem',
                         padding: '8px 12px',
                         border: `2px solid ${theme.colors.accent1}`,
                         borderRadius: theme.borderRadius.medium,
                         marginLeft: 8,
                         width: '100%'
                       }}
                     />
                   </EditDataRowShiftedEmoji>
                  ) : (
                    <>
                      <span><b>Adresse:</b> {billingData.street}</span>
                    </>
                  )}
                </DataRow>
                {!isEditingBilling && (
                  <DataRow style={{ marginLeft: 38 }}>
                    <span><b>PLZ / Stadt:</b> {billingData.zipCode && billingData.city
                      ? `${billingData.zipCode}, ${billingData.city}`
                      : billingData.zipCode || billingData.city || ''}</span>
                  </DataRow>
                )}
                {isEditingBilling && (
                  <>
                   <EditDataRowShifted style={{ marginLeft: 38 }}>
                     <span className="label" style={{minWidth: '200px'}}><b>PLZ:</b></span>
                     <input
                       type="text"
                       name="zipCode"
                       value={billingData.zipCode}
                       onChange={handleBillingChange}
                       className="input"
                       style={{
                         fontSize: '1.1rem',
                         padding: '8px 12px',
                         border: `2px solid ${theme.colors.accent1}`,
                         borderRadius: theme.borderRadius.medium,
                         marginLeft: 8,
                         width: 'calc(100% - 260px)'
                       }}
                     />
                   </EditDataRowShifted>
                   <EditDataRowShifted style={{ marginLeft: 38 }}>
                     <span className="label" style={{minWidth: '200px'}}><b>Stadt:</b></span>
                     <input
                       type="text"
                       name="city"
                       value={billingData.city}
                       onChange={handleBillingChange}
                       className="input"
                       style={{
                         fontSize: '1.1rem',
                         padding: '8px 12px',
                         border: `2px solid ${theme.colors.accent1}`,
                         borderRadius: theme.borderRadius.medium,
                         marginLeft: 8,
                         width: 'calc(100% - 260px)'
                       }}
                     />
                   </EditDataRowShifted>
                  </>
                )}
              {isEditingBilling ? (
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap'}}>
                <SecondaryDataManageButton editing={false} onClick={handleBillingCancel} disabled={billingDataLoading}>
                  Abbrechen
                </SecondaryDataManageButton>
                <DataManageButton editing onClick={handleBillingSave} disabled={billingDataLoading}>
                  {billingDataLoading ? 'Speichere...' : 'Speichern'}
                </DataManageButton>
              </div>
              ) : (
                <DataManageButton editing={false} onClick={handleBillingEdit}>Bearbeiten</DataManageButton>
              )}
            </LessonCard>
            
            {/* Account Closure Section */}
            <div style={{ marginTop: '60px', textAlign: 'center' , marginBottom: '-20px'}}>
              <SecondaryButton onClick={() => setIsAccountClosureOpen(true)}>
                Benutzerkonto schließen
              </SecondaryButton>
            </div>
          </Container>
        </>
      )}
      
      <LessonBookingPopup 
        isOpen={isLessonBookingOpen}
        onClose={handleCloseLessonBooking}
        onLessonBooked={handleLessonBooked}
        title="Neue Stunde buchen"
        availableBudgets={pageData?.budgets || []}
      />
      
      <ConfirmationPopup
        isOpen={isAccountClosureOpen}
        onClose={handleAccountClosureClose}
        onConfirm={handleAccountClosure}
        title="Benutzerkonto schließen"
        message="Das Schließen deines Benutzerkontos ist eine dauerhafte Aktion. Du wirst alle deinen verbleibenden Stundenbudgets verlieren und keine weiteren Stunden mehr buchen können. Diese Aktion kann nicht rückgängig gemacht werden."
        confirmText="Konto schließen"
        cancelText="Abbrechen"
        isDestructive={true}
        isLoading={isClosingAccount}
        showTextArea={true}
        prefilledText={confirmationText}
        onTextChange={setConfirmationText}
        placeholder="Bitte gib 'Ich verstehe' ein, um fortzufahren"
        isConfirmDisabled={confirmationText !== "Ich verstehe"}
        errorMessage={accountClosureError}
      />
    </PageLayout>
  );
};

export default MyPage; 